import {
  type Configuration,
  PublicClientApplication,
  LogLevel,
} from '@azure/msal-browser'

// =============================================================================
// Entra External ID (CIAM) configuration
// =============================================================================
// Three explicit env vars — no derivation from the tenant subdomain. Trying to
// build the authority from a short name like "jarvis" is what caused the
// previous round of login bugs (the ciamlogin subdomain and the onmicrosoft
// domain can differ, and the External tenant ID is unrelated to the workforce
// tenant ID of the same name).
//
// VITE_ENTRA_CLIENT_ID       — app registration GUID
// VITE_ENTRA_AUTHORITY       — full https://{sub}.ciamlogin.com/{tenantId} URL
// VITE_ENTRA_KNOWN_AUTHORITY — bare host (e.g. jarvis.ciamlogin.com)
// VITE_ENTRA_SCOPE           — full scope URI (api://{appIdUri}/jarvis.access)
//
// All four must be set together for real auth; absence of CLIENT_ID drops the
// app into mock mode for local dev.
// =============================================================================

const CLIENT_ID = import.meta.env.VITE_ENTRA_CLIENT_ID || ''
const AUTHORITY = import.meta.env.VITE_ENTRA_AUTHORITY || ''
const KNOWN_AUTHORITY =
  import.meta.env.VITE_ENTRA_KNOWN_AUTHORITY ||
  (AUTHORITY ? new URL(AUTHORITY).host : '')
const SCOPE = import.meta.env.VITE_ENTRA_SCOPE || ''

export const HAS_AUTH = Boolean(CLIENT_ID && AUTHORITY)

if (!HAS_AUTH && import.meta.env.PROD) {
  // Production should never be in mock mode — surface it loudly.
  // eslint-disable-next-line no-console
  console.warn(
    '[auth] VITE_ENTRA_CLIENT_ID or VITE_ENTRA_AUTHORITY missing — dashboard is running in mock mode in production. Build with .env.production set.',
  )
}

export const msalConfig: Configuration = {
  auth: {
    clientId: CLIENT_ID,
    authority: AUTHORITY,
    knownAuthorities: KNOWN_AUTHORITY ? [KNOWN_AUTHORITY] : [],
    redirectUri: typeof window !== 'undefined' ? window.location.origin : 'http://localhost:5173',
    postLogoutRedirectUri:
      typeof window !== 'undefined' ? `${window.location.origin}/login` : 'http://localhost:5173/login',
    // navigateToLoginRequestUrl: true caused a redirect loop — after Entra
    // sends the user to redirectUri (origin), MSAL would window.location.assign()
    // back to /login (where Sign In was clicked) and the LoginPage re-rendered
    // with isAuthenticated briefly false. Leaving them at origin lets the
    // ProtectedRoute on / pick up the (now-cached) authenticated state cleanly.
    navigateToLoginRequestUrl: false,
  },
  cache: {
    cacheLocation: 'sessionStorage',
    storeAuthStateInCookie: false,
  },
  system: {
    loggerOptions: {
      logLevel: import.meta.env.DEV ? LogLevel.Warning : LogLevel.Error,
      loggerCallback: (level: LogLevel, message: string) => {
        if (level === LogLevel.Error) console.error(`[MSAL] ${message}`)
      },
      piiLoggingEnabled: false,
    },
  },
}

const baseScopes = ['openid', 'profile', 'email']
export const loginRequest = {
  scopes: SCOPE ? [SCOPE, ...baseScopes] : baseScopes,
}

export const msalInstance = new PublicClientApplication(msalConfig)

export async function getAccessToken(): Promise<string> {
  const accounts = msalInstance.getAllAccounts()
  if (accounts.length === 0) {
    throw new Error('Not authenticated')
  }
  try {
    const result = await msalInstance.acquireTokenSilent({
      ...loginRequest,
      account: accounts[0],
    })
    return result.accessToken
  } catch {
    await msalInstance.acquireTokenRedirect(loginRequest)
    throw new Error('Token acquisition redirected')
  }
}
